import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { ChevronLeft, Gift, DollarSign, Globe, Clock, PlayCircle, User, Loader2, Music, Video, Leaf, HelpCircle, Layers, Heart, Zap, ArrowRight, Info, FileText } from "lucide-react";

const KIND_ICON = {
  audio: Music, video: Video, breathwork: Leaf, questions: HelpCircle, combo: Layers,
  body_scan: Heart, gratitude: Heart, visualization: Zap, movement: ArrowRight, check_in: Info, journal: FileText,
};

const KINDS = new Set(["audio", "video", "breathwork", "questions", "combo", "body_scan", "gratitude", "visualization", "movement", "check_in", "journal"]);

export default function UnlockedMethods() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("unlocked_title"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const kindLabel = (k) => (KINDS.has(k) ? t(`method_kind_${k}`) : k);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/methods/my-unlocked");
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="unlocked-methods">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="ul-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("unlocked_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("unlocked_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-center">
          <Gift className="h-8 w-8 text-muted-foreground/60" />
          <p className="max-w-sm text-sm text-muted-foreground">{t("unlocked_empty")}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((m) => {
            const Icon = KIND_ICON[m.kind] || Layers;
            const src = m.unlock_source;
            const SrcIcon = src === "coach_gift" ? Gift : src === "purchase" ? DollarSign : Globe;
            const srcLabel = src === "coach_gift" ? t("unlock_src_gift") : src === "purchase" ? t("unlock_src_paid") : t("unlock_src_public");
            return (
              <button key={m.id} data-testid={`ul-open-${m.id}`} onClick={() => navigate(`/app/method/${m.id}`)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-secondary/60 p-3.5 text-left transition-colors hover:bg-accent">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-5 w-5 text-primary" /></span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-semibold text-foreground">{m.title}</span>
                  {m.coach_name ? <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" /> {m.coach_name}</span> : null}
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{kindLabel(m.kind)}</span>
                    {m.duration_min ? <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground"><Clock className="h-2.5 w-2.5" />{m.duration_min}m</span> : null}
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary"><SrcIcon className="h-2.5 w-2.5" />{srcLabel}</span>
                  </span>
                </span>
                <PlayCircle className="h-6 w-6 shrink-0 text-primary" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
